import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { debounce, isEmpty, isEqual } from 'lodash';
import { useStoreActions, useStoreState } from '../models/RootStore';
import { useSetBacnetObjectValue } from './useBacnetDevicesApi';
import { useSetModbusUnitRegisterValue } from './useModbusDevicesApi';
import { getProtocolAdapter, ProtocolType } from '../utils/universalUnit/protocols';
import {
  toUnitShape,
  resolveRawModeValue,
  resolveRawFanModeValue,
  resolveRawSwingModeValue,
} from '../utils/universalUnit/toUnitShape';
import { buildUnitPanelStoreProps } from '../utils/universalUnit/unitPanelProps';
import { getUnitActiveSchedulesCount } from '../utils/CommonUtils';
import { UNIVERSAL_PROPERTIES } from '../utils/universalUnit/properties';
import { withLatestOperationalData } from '../utils/latestOperationalData';

export interface UseUniversalUnitShapeOptions {
  /** The device entry (BACnet or Modbus) */
  entry: any;
  /** Parameter template (for BACnet) */
  paramTemplate?: any;
  /** Rows (for Modbus) */
  rows?: any[];
  /** Navigation callback to open schedules */
  navigateToSchedules?: (siteId?: string, unitId?: string) => void;
  /** Whether the temperature scale is Celsius */
  isCelsius?: boolean;
  /** Protocol identifier ('bacnet' | 'modbus') */
  protocol?: ProtocolType;
  /** All schedules map/array for active schedules calculation */
  allSchedules?: any;
  /** All groups map/array for active schedules calculation */
  allGroups?: any;
}

/**
 * Hook that manages universal unit state, real-time WebSocket subscriptions,
 * debounced setpoint writes, and provides control handlers for UnitPanel.
 */
export const useUniversalUnitShape = ({
  entry,
  paramTemplate,
  rows,
  navigateToSchedules,
  isCelsius: propIsCelsius,
  protocol = 'bacnet',
  allSchedules,
  allGroups,
}: UseUniversalUnitShapeOptions) => {
  const unitId = entry?.id || '';
  const adapter = useMemo(() => getProtocolAdapter(protocol), [protocol]);

  // Start from what arrived over the websocket since the entry was fetched, not just the entry itself
  const [operationalData, setOperationalData] = useState<Record<string, any>>(() =>
    withLatestOperationalData(unitId, entry?.operationalData || {})
  );

  const types = useStoreState((state) => state.types);
  const operationModesMirror = useStoreState((state) => state.operationModesMirror);
  const operationStatusesMirror = useStoreState((state) => state.operationStatusesMirror);
  const temperatureScaleMirror = useStoreState((state) => state.temperatureScaleMirror);
  const fanModesMirror = useStoreState((state) => state.fanModesMirror);
  const swingModesMirror = useStoreState((state) => state.swingModesMirror);
  const user = useStoreState((state) => state.users.me);

  const isCelsius =
    propIsCelsius !== undefined
      ? propIsCelsius
      : temperatureScaleMirror && user && user.temperatureScale !== undefined
      ? +temperatureScaleMirror.celsius === +user.temperatureScale
      : true;

  const addWebSocketListener = useStoreActions((actions: any) => actions.addWebSocketListener);
  const removeWebSocketListener = useStoreActions((actions: any) => actions.removeWebSocketListener);
  const broadcastWebSocketMessage = useStoreActions(
    (actions: any) => actions.broadcastWebSocketMessage
  );
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  // Protocol-specific mutations
  const { mutate: setBacnetObjectValue } = (useSetBacnetObjectValue() || {}) as any;
  const { mutate: setModbusRegisterValue } = useSetModbusUnitRegisterValue({
    onError: (err: any) => addMessage({ message: err?.message || 'Failed to update Modbus value' }),
  });

  // Synchronize when entry.operationalData changes externally
  useEffect(() => {
    if (!entry?.operationalData) {
      return;
    }
    const next = withLatestOperationalData(unitId, entry.operationalData);
    if (!isEqual(operationalData, next)) {
      setOperationalData(next);
    }
  }, [entry?.operationalData]);

  // WebSocket live updates
  useEffect(() => {
    const handleWebSocketMessage = (message: any) => {
      const isBacnetMsg =
        protocol === 'bacnet' &&
        message.name === 'UPDATE_BACNET_UNIT_DATA' &&
        String(message.data?.unitId) === String(unitId);
      const isModbusMsg =
        protocol === 'modbus' &&
        message.name === 'UPDATE_MODBUS_UNIT_DATA' &&
        String(message.data?.unitId) === String(unitId);

      if (!isBacnetMsg && !isModbusMsg) {
        return;
      }

      const newOperationalData = message.data?.operationalData;
      if (isEmpty(newOperationalData)) {
        return;
      }

      setOperationalData((prev) => {
        if (isEqual(prev, newOperationalData)) {
          return prev;
        }
        return {
          ...prev,
          ...newOperationalData,
        };
      });
    };

    addWebSocketListener(handleWebSocketMessage);
    return () => {
      removeWebSocketListener(handleWebSocketMessage);
    };
  }, [unitId, protocol, addWebSocketListener, removeWebSocketListener]);

  // Mirrors operationalData so an optimistic write can build the merged object
  // outside a state updater — broadcasting from inside one would fire the side
  // effect twice under StrictMode.
  const operationalDataRef = useRef(operationalData);
  operationalDataRef.current = operationalData;

  // Apply an optimistic value locally AND announce it on the same channel the
  // real device updates arrive on, so the control panel and the list item stay
  // in step instead of each holding a private copy until the device reports back.
  const applyOptimisticValue = useCallback(
    (pointKey: string, value: any) => {
      const merged = {
        ...operationalDataRef.current,
        [pointKey]: value,
      };
      operationalDataRef.current = merged;
      setOperationalData(merged);

      broadcastWebSocketMessage({
        name: protocol === 'modbus' ? 'UPDATE_MODBUS_UNIT_DATA' : 'UPDATE_BACNET_UNIT_DATA',
        data: {
          unitId,
          operationalData: merged,
        },
      });
    },
    [broadcastWebSocketMessage, protocol, unitId]
  );

  const sourceData =
    protocol === 'modbus'
      ? rows || entry?.rows || entry?.paramTemplateData?.rows || []
      : paramTemplate?.data || entry?.paramTemplateData || {};

  const universalMappedPoints = useMemo(
    () => adapter.getUniversalMappedPoints(sourceData),
    [adapter, sourceData]
  );

  const isUniversalUnit =
    protocol === 'modbus'
      ? (entry?.entityTemplate ?? entry?.paramTemplateData?.entityTemplate ?? false) === true
      : (paramTemplate?.entityTemplate ?? entry?.entityTemplate ?? entry?.paramTemplateData?.entityTemplate ?? false) === true;

  const hasUniversalControls = Object.keys(universalMappedPoints).length > 0;

  const typesStoreCombined = useMemo(
    () => ({
      types,
      operationModesMirror,
      operationStatusesMirror,
      fanModesMirror,
      swingModesMirror,
      operationModesExtended: types?.operationModesExtended,
    }),
    [types, operationModesMirror, operationStatusesMirror, fanModesMirror, swingModesMirror]
  );

  // Synthesize unit shape
  const synthesizedUnit = useMemo(() => {
    return toUnitShape({
      entry,
      paramTemplate,
      rows,
      operationalData,
      typesStore: typesStoreCombined,
      isCelsius,
      protocol,
    });
  }, [entry, paramTemplate, rows, operationalData, typesStoreCombined, isCelsius, protocol]);

  // Debounced Setpoint write
  const debouncedWriteSetpoint = useMemo(
    () =>
      debounce(async (targetUnitId: string, point: any, value: any) => {
        if (protocol === 'bacnet') {
          if (!point?.objectId) {
            return;
          }
          const parsedValue = adapter.prepareValue ? adapter.prepareValue(point.objectId, value) : value;
          setBacnetObjectValue(
            {
              unitId: targetUnitId,
              payload: { object: point.objectId, value: parsedValue },
            },
            {
              onError: (err: any) => addMessage({ message: err?.message || 'Failed to update BACnet value' }),
            }
          );
        } else if (protocol === 'modbus') {
          const row = point?.row || point;
          if (!row) {
            return;
          }
          const parsedValue = adapter.prepareValue ? adapter.prepareValue(row, value) : value;
          setModbusRegisterValue({
            modbusUnitId: targetUnitId,
            data: {
              register: { type: row.registerType, address: row.registerAddress, ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}) },
              value: parsedValue,
            },
          });
        }
      }, 1000),
    [protocol, adapter, setBacnetObjectValue, setModbusRegisterValue, addMessage]
  );

  // Flush debounce on unmount
  useEffect(() => {
    return () => {
      debouncedWriteSetpoint.flush();
    };
  }, [debouncedWriteSetpoint]);

  // Power action handler
  const handleSetActiveOperationStatus = useCallback(
    async ({ unitId: targetUnitId, state: newState }: { unitId?: string; state: any }) => {
      const point = universalMappedPoints[UNIVERSAL_PROPERTIES.OPERATION_STATUS];
      if (!point) {
        return;
      }

      const effectiveUnitId = targetUnitId || unitId;
      // UnitPanel hands back a value from the app's operationStatuses enum, not
      // a raw 1/0 — resolve it against the mirror before writing to the device.
      const onVal = operationStatusesMirror?.on;
      const offVal = operationStatusesMirror?.off;
      let isPowerOn;
      if (onVal !== undefined && String(newState) === String(onVal)) {
        isPowerOn = true;
      } else if (offVal !== undefined && String(newState) === String(offVal)) {
        isPowerOn = false;
      } else {
        isPowerOn =
          newState === 1 ||
          newState === '1' ||
          newState === true ||
          newState === 'ON' ||
          newState === 'on';
      }
      const rawValueToSend = isPowerOn ? 1 : 0;

      if (protocol === 'bacnet') {
        if (!point.objectId) {
          return;
        }
        const pointKey = adapter.buildPointKey(point.objectId);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(point.objectId, rawValueToSend)
          : rawValueToSend;

        applyOptimisticValue(pointKey, parsedValue);

        setBacnetObjectValue(
          {
            unitId: effectiveUnitId,
            payload: { object: point.objectId, value: parsedValue },
          },
          {
            onError: (err: any) => addMessage({ message: err?.message || 'Failed to update power status' }),
          }
        );
      } else if (protocol === 'modbus') {
        const row = point.row || point;
        if (!row) {
          return;
        }
        const pointKey = adapter.buildPointKey(row);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(row, rawValueToSend)
          : rawValueToSend;

        applyOptimisticValue(pointKey, parsedValue);

        setModbusRegisterValue({
          modbusUnitId: effectiveUnitId,
          data: {
            register: { type: row.registerType, address: row.registerAddress, ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}) },
            value: parsedValue,
          },
        });
      }
    },
    [universalMappedPoints, unitId, protocol, adapter, operationStatusesMirror, applyOptimisticValue, setBacnetObjectValue, setModbusRegisterValue, addMessage]
  );

  // Setpoint action handler
  const handleSetActiveSetpoint = useCallback(
    async ({ id: targetUnitId, setpoint: newSetpoint, data }: { id?: string; setpoint?: any; data?: any }) => {
      const point = universalMappedPoints[UNIVERSAL_PROPERTIES.SETPOINT];
      if (!point) {
        return;
      }

      const effectiveUnitId = targetUnitId || unitId;
      const value = newSetpoint !== undefined ? newSetpoint : data;
      const pointKey =
        protocol === 'bacnet' ? adapter.buildPointKey(point.objectId) : adapter.buildPointKey(point.row || point);

      applyOptimisticValue(pointKey, value);

      debouncedWriteSetpoint(effectiveUnitId, point, value);
    },
    [universalMappedPoints, unitId, protocol, adapter, debouncedWriteSetpoint]
  );

  // Mode action handler
  const handleSetActiveOperationMode = useCallback(
    async ({ id: targetUnitId, mode: newMode, data }: { id?: string; mode?: any; data?: any }) => {
      const point = universalMappedPoints[UNIVERSAL_PROPERTIES.OPERATION_MODE];
      if (!point) {
        return;
      }

      const effectiveUnitId = targetUnitId || unitId;
      const modeVal = newMode !== undefined ? newMode : data;
      const rawModeValue = resolveRawModeValue(point, Number(modeVal), typesStoreCombined);

      if (protocol === 'bacnet') {
        if (!point.objectId) {
          return;
        }
        const pointKey = adapter.buildPointKey(point.objectId);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(point.objectId, rawModeValue)
          : rawModeValue;

        applyOptimisticValue(pointKey, parsedValue);

        setBacnetObjectValue(
          {
            unitId: effectiveUnitId,
            payload: { object: point.objectId, value: parsedValue },
          },
          {
            onError: (err: any) => addMessage({ message: err?.message || 'Failed to update operation mode' }),
          }
        );
      } else if (protocol === 'modbus') {
        const row = point.row || point;
        if (!row) {
          return;
        }
        const pointKey = adapter.buildPointKey(row);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(row, rawModeValue)
          : rawModeValue;

        applyOptimisticValue(pointKey, parsedValue);

        setModbusRegisterValue({
          modbusUnitId: effectiveUnitId,
          data: {
            register: { type: row.registerType, address: row.registerAddress, ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}) },
            value: parsedValue,
          },
        });
      }
    },
    [universalMappedPoints, unitId, protocol, adapter, typesStoreCombined, setBacnetObjectValue, setModbusRegisterValue, addMessage]
  );

  // Fan Mode action handler
  const handleSetActiveFanMode = useCallback(
    async ({ unitId: targetUnitId, id, data, mode }: { unitId?: string; id?: string; data?: any; mode?: any }) => {
      const point = universalMappedPoints[UNIVERSAL_PROPERTIES.FAN_MODE];
      if (!point) {
        return;
      }

      const effectiveUnitId = targetUnitId || id || unitId;
      const fanVal = data !== undefined ? data : mode;
      const rawFanValue = resolveRawFanModeValue(point, Number(fanVal), typesStoreCombined);

      if (protocol === 'bacnet') {
        if (!point.objectId) {
          return;
        }
        const pointKey = adapter.buildPointKey(point.objectId);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(point.objectId, rawFanValue)
          : rawFanValue;

        applyOptimisticValue(pointKey, parsedValue);

        setBacnetObjectValue(
          {
            unitId: effectiveUnitId,
            payload: { object: point.objectId, value: parsedValue },
          },
          {
            onError: (err: any) => addMessage({ message: err?.message || 'Failed to update fan mode' }),
          }
        );
      } else if (protocol === 'modbus') {
        const row = point.row || point;
        if (!row) {
          return;
        }
        const pointKey = adapter.buildPointKey(row);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(row, rawFanValue)
          : rawFanValue;

        applyOptimisticValue(pointKey, parsedValue);

        setModbusRegisterValue({
          modbusUnitId: effectiveUnitId,
          data: {
            register: { type: row.registerType, address: row.registerAddress, ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}) },
            value: parsedValue,
          },
        });
      }
    },
    [universalMappedPoints, unitId, protocol, adapter, typesStoreCombined, applyOptimisticValue, setBacnetObjectValue, setModbusRegisterValue, addMessage]
  );

  // Swing Mode action handler
  const handleSetActiveSwingMode = useCallback(
    async ({ unitId: targetUnitId, id, data, mode }: { unitId?: string; id?: string; data?: any; mode?: any }) => {
      const point = universalMappedPoints[UNIVERSAL_PROPERTIES.SWING_MODE];
      if (!point) {
        return;
      }

      const effectiveUnitId = targetUnitId || id || unitId;
      const swingVal = data !== undefined ? data : mode;
      const rawSwingValue = resolveRawSwingModeValue(point, Number(swingVal), typesStoreCombined);

      if (protocol === 'bacnet') {
        if (!point.objectId) {
          return;
        }
        const pointKey = adapter.buildPointKey(point.objectId);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(point.objectId, rawSwingValue)
          : rawSwingValue;

        applyOptimisticValue(pointKey, parsedValue);

        setBacnetObjectValue(
          {
            unitId: effectiveUnitId,
            payload: { object: point.objectId, value: parsedValue },
          },
          {
            onError: (err: any) => addMessage({ message: err?.message || 'Failed to update swing mode' }),
          }
        );
      } else if (protocol === 'modbus') {
        const row = point.row || point;
        if (!row) {
          return;
        }
        const pointKey = adapter.buildPointKey(row);
        const parsedValue = adapter.prepareValue
          ? adapter.prepareValue(row, rawSwingValue)
          : rawSwingValue;

        applyOptimisticValue(pointKey, parsedValue);

        setModbusRegisterValue({
          modbusUnitId: effectiveUnitId,
          data: {
            register: { type: row.registerType, address: row.registerAddress, ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}) },
            value: parsedValue,
          },
        });
      }
    },
    [universalMappedPoints, unitId, protocol, adapter, typesStoreCombined, applyOptimisticValue, setBacnetObjectValue, setModbusRegisterValue, addMessage]
  );

  const activeSchedulesCount = useMemo(() => {
    return getUnitActiveSchedulesCount(entry, { allSchedules, allGroups });
  }, [entry, allSchedules, allGroups]);

  const handleNavigateToSchedules = useCallback(
    (siteId?: string, targetUnitId?: string) => {
      if (navigateToSchedules) {
        navigateToSchedules(siteId, targetUnitId || unitId);
      }
    },
    [navigateToSchedules, unitId]
  );

  // UnitPanel is normally rendered by Control.tsx, which feeds it the type
  // enums and mirrors from the store. A control panel rendering it as the
  // universal fallback has to supply the same set — without them UnitPanel
  // cannot resolve on/off values and every power toggle resolves to "off".
  const unitPanelStoreProps = useMemo(
    () =>
      buildUnitPanelStoreProps({
        types,
        operationModesMirror,
        operationStatusesMirror,
        fanModesMirror,
        swingModesMirror,
        temperatureScale: user?.temperatureScale,
      }),
    [types, operationModesMirror, operationStatusesMirror, fanModesMirror, swingModesMirror, user?.temperatureScale]
  );

  return {
    operationalData,
    synthesizedUnit,
    isUniversalUnit,
    hasUniversalControls,
    isReady: isUniversalUnit && hasUniversalControls,
    activeSchedulesCount,
    unitPanelStoreProps,
    handleSetActiveOperationStatus,
    handleSetActiveSetpoint,
    handleSetActiveOperationMode,
    handleSetActiveFanMode,
    handleSetActiveSwingMode,
    handleNavigateToSchedules,
  };
};

export default useUniversalUnitShape;

